// src/pages/Track.js
import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { trackShipment } from "../services/api";

const STATUS_LABELS = {
  pending:           "Order Placed",
  picked_up:         "Picked Up",
  at_hub:            "At Sorting Hub",
  in_transit:        "In Transit",
  out_for_delivery:  "Out for Delivery",
  delivered:         "Delivered",
  cancelled:         "Cancelled",
};

const statusStyle = s => ({
  delivered:         { color: "#16A34A", bg: "#DCFCE7" },
  in_transit:        { color: "#00A896", bg: "rgba(0,198,215,.1)" },
  out_for_delivery:  { color: "#00A896", bg: "rgba(0,198,215,.1)" },
  pending:           { color: "#D97706", bg: "#FEF3C7" },
  picked_up:         { color: "#D97706", bg: "#FEF3C7" },
  at_hub:            { color: "#D97706", bg: "#FEF3C7" },
  cancelled:         { color: "#DC2626", bg: "#FEE2E2" },
}[s] || { color: "#6B7280", bg: "#EEF4F7" });

export default function Track() {
  const [searchParams] = useSearchParams();
  const [awb, setAwb]         = useState(searchParams.get("awb") || "");
  const [result, setResult]   = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");

  useEffect(() => {
    if (searchParams.get("awb")) doTrack(searchParams.get("awb"));
  }, []);

  const doTrack = async (val = awb) => {
    if (!val.trim()) return;
    setLoading(true); setError(""); setResult(null);
    const data = await trackShipment(val.trim().toUpperCase());
    setLoading(false);
    if (data?.success) setResult(data);
    else setError("Shipment not found. Check your AWB and try again.");
  };

  const ship = result?.shipment;
  const timeline = result?.timeline || [];

  return (
    <div style={{ background: "#F7FAFB", minHeight: "100vh" }}>
      {/* Header */}
      <div className="dark-header">
        <div style={{ maxWidth: 680, margin: "0 auto" }}>
          <span className="tag tag-dark" style={{ marginBottom: 14, display: "inline-block" }}>ORDER TRACKING</span>
          <h1>Track your shipment</h1>
          <p style={{ marginBottom: 28 }}>Enter your AWB or tracking ID for real-time status.</p>
          <div style={{ display: "flex", background: "rgba(255,255,255,.1)", border: "1px solid rgba(255,255,255,.15)", borderRadius: 10, overflow: "hidden" }}>
            <input value={awb} onChange={e => setAwb(e.target.value)}
              placeholder="e.g. PL-2025-00001"
              onKeyDown={e => e.key === "Enter" && doTrack()}
              style={{ flex: 1, border: "none", background: "transparent", color: "#fff", padding: "13px 16px", fontSize: 14, outline: "none" }} />
            <button className="btn btn-primary" onClick={() => doTrack()} disabled={loading}
              style={{ borderRadius: 0, padding: "0 28px" }}>
              {loading ? "Searching…" : "Track →"}
            </button>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 680, margin: "0 auto", padding: "32px 24px" }}>
        {loading && <div className="spinner" />}

        {error && (
          <div className="card fade-in" style={{ textAlign: "center", padding: "48px 24px" }}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>🔍</div>
            <div style={{ fontFamily: "'Syne',sans-serif", fontWeight: 700, fontSize: 18, color: "#0B1E3D", marginBottom: 8 }}>Shipment not found</div>
            <div style={{ color: "#6B7280", fontSize: 14 }}>{error}</div>
          </div>
        )}

        {ship && (
          <div className="fade-in">
            {/* Status card */}
            <div className="card" style={{ marginBottom: 14 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 16 }}>
                <div>
                  <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 11, color: "#9CA3AF", marginBottom: 8 }}>AWB · {ship.awb}</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ padding: "5px 14px", borderRadius: 20, fontWeight: 600, fontSize: 13, background: statusStyle(ship.status).bg, color: statusStyle(ship.status).color }}>
                      {STATUS_LABELS[ship.status] || ship.status}
                    </span>
                    {ship.status === "in_transit" && <span className="live-dot" />}
                  </div>
                </div>
                {ship.eta && (
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 12, color: "#6B7280", marginBottom: 4 }}>ETA</div>
                    <div style={{ fontFamily: "'Syne',sans-serif", fontWeight: 700, fontSize: 20, color: "#0B1E3D" }}>
                      {new Date(ship.eta).toLocaleDateString("en-NG", { day: "numeric", month: "long", year: "numeric" })}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Route card */}
            <div className="card" style={{ marginBottom: 14 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", alignItems: "center", gap: 12, marginBottom: 20 }}>
                <div>
                  <div style={{ fontSize: 11, color: "#9CA3AF", fontFamily: "'JetBrains Mono',monospace", marginBottom: 4 }}>FROM</div>
                  <div style={{ fontWeight: 600, color: "#0B1E3D" }}>{ship.sender_address}</div>
                </div>
                <div style={{ width: 36, height: 36, borderRadius: "50%", background: "linear-gradient(135deg,#00C6D7,#00A896)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 16 }}>→</div>
                <div>
                  <div style={{ fontSize: 11, color: "#9CA3AF", fontFamily: "'JetBrains Mono',monospace", marginBottom: 4 }}>TO</div>
                  <div style={{ fontWeight: 600, color: "#0B1E3D" }}>{ship.recipient_address}</div>
                </div>
              </div>
              <div className="divider" />
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(110px,1fr))", gap: 12, marginTop: 16 }}>
                {[["Sender", ship.sender_name], ["Recipient", ship.recipient_name], ["Weight", ship.weight_kg ? `${ship.weight_kg}kg` : "N/A"], ["Type", ship.shipment_type]].map(([k, v]) => (
                  <div key={k}>
                    <div style={{ fontSize: 11, color: "#9CA3AF", marginBottom: 3 }}>{k}</div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#374151", textTransform: "capitalize" }}>{v}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Timeline */}
            <div className="card">
              <div style={{ fontFamily: "'Syne',sans-serif", fontWeight: 700, fontSize: 16, color: "#0B1E3D", marginBottom: 24 }}>
                Shipment Timeline
              </div>
              {timeline.length === 0 && (
                <p style={{ color: "#9CA3AF", fontSize: 13 }}>No tracking events yet.</p>
              )}
              {timeline.map((ev, i) => (
                <div key={i} style={{ display: "flex", gap: 14, paddingBottom: i < timeline.length - 1 ? 20 : 0 }}>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                    <div style={{ width: 22, height: 22, borderRadius: "50%", flexShrink: 0, background: i === timeline.length - 1 ? "#00C6D7" : "#00A896", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, color: "#fff", fontWeight: 700 }}>✓</div>
                    {i < timeline.length - 1 && <div style={{ width: 2, flex: 1, marginTop: 4, background: "#00A896" }} />}
                  </div>
                  <div style={{ paddingTop: 2, paddingBottom: 4 }}>
                    <div style={{ fontWeight: 600, fontSize: 14, color: i === timeline.length - 1 ? "#00A896" : "#0B1E3D" }}>
                      {STATUS_LABELS[ev.status] || ev.status}
                      {i === timeline.length - 1 && <span className="tag tag-cyan" style={{ marginLeft: 8, padding: "1px 8px", fontSize: 10 }}>CURRENT</span>}
                    </div>
                    {ev.description && <div style={{ fontSize: 13, color: "#6B7280", marginTop: 2 }}>{ev.description}</div>}
                    <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 11, color: "#9CA3AF", marginTop: 2 }}>
                      {new Date(ev.created_at).toLocaleString("en-NG")}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
